import { useEffect, useRef, useState } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import { CheckCircle2, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { SharedProps } from '@/types'

type Status = 'pending' | 'paid' | 'failed' | 'expired' | 'refunded' | 'partially_refunded' | 'unknown'

type PurchaseItem = {
  item_id: string
  item_name: string | null
  quantity: number
  price: number
}

type StatusResponse = {
  status?: Status
  email?: string
  transaction_id?: string
  value?: number
  currency?: string
  kind?: 'ticket' | 'donation' | 'sponsorship' | 'ad'
  wants_account?: boolean
  items?: PurchaseItem[]
}

const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 20000

export default function CheckoutSuccess() {
  const { current_user } = usePage<SharedProps>().props
  const [status, setStatus] = useState<Status>('pending')
  const [email, setEmail] = useState<string | null>(null)
  const [wantsAccount, setWantsAccount] = useState(false)
  const [timedOut, setTimedOut] = useState(false)
  const sessionId = useRef<string | null>(null)
  const purchaseId = useRef<string | null>(null)
  const gtagFired = useRef(false)
  const redirected = useRef(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    sessionId.current = params.get('session_id')
    purchaseId.current = params.get('purchase_id')
    if (!sessionId.current && !purchaseId.current) {
      setTimedOut(true)
      return
    }

    let cancelled = false
    const startedAt = Date.now()

    async function poll() {
      try {
        const url = purchaseId.current
          ? `/checkout/purchase_status/${encodeURIComponent(purchaseId.current)}`
          : `/checkout/status/${encodeURIComponent(sessionId.current ?? '')}`
        const res = await fetch(url)
        const data = (await res.json()) as StatusResponse
        if (cancelled) return
        if (data.status) setStatus(data.status)
        if (data.email) setEmail(data.email)
        if (typeof data.wants_account === 'boolean') setWantsAccount(data.wants_account)
        if (data.status === 'paid' && !gtagFired.current && data.transaction_id) {
          gtagFired.current = true
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'purchase', {
              transaction_id: data.transaction_id,
              value: data.value,
              currency: data.currency,
              kind: data.kind,
              items: (data.items ?? []).map((it) => ({
                item_id: it.item_id,
                item_name: it.item_name ?? undefined,
                quantity: it.quantity,
                price: it.price,
              })),
            })
          }
        }
        if (data.status === 'paid' || data.status === 'failed' || data.status === 'expired') return
      } catch {
        // ignore — try again next tick
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        if (!cancelled) setTimedOut(true)
        return
      }
      setTimeout(poll, POLL_INTERVAL_MS)
    }
    poll()

    return () => {
      cancelled = true
    }
  }, [])

  const signupParams = new URLSearchParams()
  if (email) signupParams.set('email', email)
  if (sessionId.current) signupParams.set('checkout_session_id', sessionId.current)
  if (purchaseId.current) signupParams.set('purchase_id', purchaseId.current)
  const signupQuery = signupParams.toString()
  const signupHref = `/users/sign_up${signupQuery ? `?${signupQuery}` : ''}`

  useEffect(() => {
    if (redirected.current) return
    if (status !== 'paid' || current_user || !wantsAccount) return
    redirected.current = true
    window.location.assign(signupHref)
  }, [status, current_user, wantsAccount, signupHref])

  const finalState =
    status === 'paid'
      ? 'paid'
      : status === 'failed' || status === 'expired'
        ? 'problem'
        : timedOut
          ? 'timeout'
          : 'loading'

  return (
    <>
      <Head title="Order confirmed" />
      <div className="mx-auto flex min-h-[80vh] max-w-xl items-center px-4 py-16">
        <Card className="w-full">
          <CardContent className="space-y-6 py-10 text-center">
            {finalState === 'loading' && (
              <>
                <Clock className="mx-auto size-12 animate-pulse text-muted-foreground" />
                <h1 className="text-2xl font-semibold">
                  {purchaseId.current ? 'Confirming your tickets…' : 'Confirming your payment…'}
                </h1>
                <p className="text-muted-foreground">
                  This usually takes just a few seconds.
                </p>
              </>
            )}

            {finalState === 'paid' && (
              <>
                <CheckCircle2 className="mx-auto size-12 text-green-600" />
                <h1 className="text-2xl font-semibold">
                  {purchaseId.current ? 'Tickets confirmed' : 'Payment received'}
                </h1>
                <p className="text-muted-foreground">
                  Your confirmation email is on its way. Please check your inbox.
                </p>
              </>
            )}

            {finalState === 'timeout' && (
              <>
                <Clock className="mx-auto size-12 text-muted-foreground" />
                <h1 className="text-2xl font-semibold">Still working on it</h1>
                <p className="text-muted-foreground">
                  Your payment is being processed. Check your email in a few
                  minutes for confirmation. If you don't see anything, contact
                  the organizer.
                </p>
              </>
            )}

            {finalState === 'problem' && (
              <>
                <h1 className="text-2xl font-semibold">Something went wrong</h1>
                <p className="text-muted-foreground">
                  {purchaseId.current
                    ? "We couldn't confirm your tickets. Try claiming them again or contact the organizer."
                    : 'Your payment did not complete. If you were charged, your card will be refunded. Contact the organizer if you need help.'}
                </p>
              </>
            )}

            {!current_user && finalState === 'paid' && (
              <div className="rounded-md border border-black/10 bg-muted/40 p-4 text-left text-sm">
                <p className="font-medium">Track your tickets in one place</p>
                <p className="mt-1 text-muted-foreground">
                  Create an account to see all your purchases and follow this organization.
                </p>
                <Button asChild className="mt-3" variant="outline" size="sm">
                  <Link href={signupHref}>Create account</Link>
                </Button>
              </div>
            )}

            <div className="pt-2">
              <Button asChild variant="ghost">
                <Link href="/events">Browse more events</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
